import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { customerService } from '../../../services/customerService';

const RatingModal = ({ isOpen, onClose, order, onRatingSubmitted }) => {
  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [existingReview, setExistingReview] = useState(null);

  useEffect(() => {
    if (isOpen && order?.id) {
      loadExistingReview();
    }
  }, [isOpen, order?.id]);

  const loadExistingReview = async () => {
    try {
      const { data, error } = await customerService?.getReviewForOrder(order?.id);
      if (error) throw error;
      
      if (data) {
        setExistingReview(data);
        setRating(data?.rating);
        setComment(data?.comment || '');
      } else {
        setExistingReview(null);
        setRating(0);
        setComment('');
      }
    } catch (err) {
      console.error('Failed to load existing review:', err);
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (rating === 0) {
      setError('Please select a rating');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const reviewData = {
        orderId: order?.id,
        customerId: order?.customer_id,
        supplierId: order?.supplier_id,
        rating,
        comment: comment?.trim()
      };

      let result;
      if (existingReview) {
        result = await customerService?.updateReview(existingReview?.id, reviewData);
      } else {
        result = await customerService?.createReview(reviewData);
      }

      if (result?.error) throw result?.error;

      onRatingSubmitted?.();
      onClose();
    } catch (err) {
      console.error('Failed to submit review:', err);
      setError(err?.message || 'Failed to submit review. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setRating(0);
      setHoveredRating(0);
      setComment('');
      setError('');
      setExistingReview(null);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-card rounded-xl shadow-warm-md w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-heading font-bold text-2xl">
              {existingReview ? 'Update Your Review' : 'Rate Your Order'}
            </h3>
            <button
              onClick={handleClose}
              disabled={loading}
              className="p-2 hover:bg-muted rounded-lg transition-smooth disabled:opacity-50"
            >
              <Icon name="X" size={20} />
            </button>
          </div>

          {order && (
            <div className="bg-muted/50 rounded-lg p-4 mb-6">
              <p className="text-sm text-muted-foreground mb-1">
                Order #{order?.order_number || order?.orderId || 'N/A'}
              </p>
              <p className="font-heading font-semibold">{order?.supplier?.business_name || order?.supplierName}</p>
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="mb-6">
              <label className="block text-sm font-medium mb-3">
                How would you rate your experience?
              </label>
              <div className="flex items-center justify-center gap-2">
                {[1, 2, 3, 4, 5]?.map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => setRating(star)}
                    onMouseEnter={() => setHoveredRating(star)}
                    onMouseLeave={() => setHoveredRating(0)}
                    className="p-2 transition-smooth hover:scale-110"
                  >
                    <Icon
                      name="Star"
                      size={32}
                      color={star <= (hoveredRating || rating) ? 'var(--color-warning)' : 'var(--color-muted)'}
                      className={star <= (hoveredRating || rating) ? 'fill-current' : ''}
                    />
                  </button>
                ))}
              </div>
              {rating > 0 && (
                <p className="text-center text-sm text-muted-foreground mt-2">
                  {rating === 1 && 'Poor'}
                  {rating === 2 && 'Fair'}
                  {rating === 3 && 'Good'}
                  {rating === 4 && 'Very Good'}
                  {rating === 5 && 'Excellent'}
                </p>
              )}
            </div>

            <div className="mb-6">
              <label htmlFor="comment" className="block text-sm font-medium mb-2">
                Share your experience (optional)
              </label>
              <textarea
                id="comment"
                value={comment}
                onChange={(e) => setComment(e?.target?.value)}
                placeholder="Tell us about your order, food quality, delivery experience..."
                rows={4}
                className="w-full px-4 py-3 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring resize-none"
                disabled={loading}
              />
            </div>

            {error && (
              <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-3 mb-4">
                <div className="flex items-center gap-2 text-destructive text-sm">
                  <Icon name="AlertCircle" size={16} />
                  <p>{error}</p>
                </div>
              </div>
            )}

            <div className="flex gap-3">
              <Button
                type="button"
                variant="outline"
                fullWidth
                onClick={handleClose}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                fullWidth
                disabled={loading || rating === 0}
                iconName={loading ? undefined : 'Send'}
              >
                {loading ? 'Submitting...' : existingReview ? 'Update Review' : 'Submit Review'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default RatingModal;